import type {Ref, ComputedRef} from 'vue';
import {ref, computed} from 'vue';
import {eachWeekOfInterval, format, addDays} from 'date-fns';
import {useTranslation} from '#imports';

import type {Day, Week, Days, IntRange} from '@/common/types';

export const useGetWeeksOfMonth = (
    month: number,
    year: number,
): {
    weeksOfTheMonth: Ref<
        {
            daysOfWeek: Days;
            weekRangeText: string;
        }[]
    >;
    daysOfTheMonth: ComputedRef<Days>;
} => {
    const {t} = useTranslation();

    const defaultWorkedHour = 8;
    const defaultWorkedDays = '1';
    const numberOfDaysInAWeek = 7;
    const mondayIndex = 1;

    const weeksOfTheMonth = ref<Week[]>([]);
    const firstDate: Date = new Date(year, month, 1);
    const lastDate: Date = new Date(year, month + 1, 0);
    const firstDayOfEachWeek: Date[] = eachWeekOfInterval({start: firstDate, end: lastDate}, {weekStartsOn: mondayIndex});

    weeksOfTheMonth.value = firstDayOfEachWeek.map((firstDayOfTheWeek) => {
        const daysOfWeek = Array.from({length: numberOfDaysInAWeek}, (_, weeklyDailyIndex) => {
            const dayOfTheWeek = addDays(firstDayOfTheWeek, weeklyDailyIndex);

            return {
                weeklyDailyIndex,
                fullDate: dayOfTheWeek,
                dayNumber: dayOfTheWeek.getDate(),
                monthName: t(`month.cut.${(month + 1) as IntRange<1, 12>}`),
                dayName: t(`day.${(weeklyDailyIndex + 1) as IntRange<1, 7>}`),
                dayShortName: t(`day.cut.${(weeklyDailyIndex + 1) as IntRange<1, 7>}`),
                monthNumber: dayOfTheWeek.getMonth() + 1,
                workedHours: defaultWorkedHour,
                workedHoursDetails: '',
                workedDays: defaultWorkedDays,
                workedDaysDetails: '',
                hasWorked: false,
            };
        }).filter((day: Day) => day.monthNumber === month + 1);

        const formattedWeekStartDate = format(daysOfWeek[0].fullDate, 'EEE dd/MM');
        const formattedWeekEndDate = format(daysOfWeek[daysOfWeek.length - 1].fullDate, 'EEE dd/MM');
        const weekRangeText = `${formattedWeekStartDate} ${t('activity-tracker.to')} ${formattedWeekEndDate}`;

        return {
            daysOfWeek,
            weekRangeText,
        };
    });

    const daysOfTheMonth = computed(() => weeksOfTheMonth.value.flatMap((week: Week) => week.daysOfWeek));

    return {
        weeksOfTheMonth,
        daysOfTheMonth,
    };
};
